import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Lock,
  TrendingUp,
  TrendingDown,
  Crown,
  Trophy,
  Activity,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

import { API_BASE } from "../lib/api"; 
import { safeJson } from "../lib/api";
export default function SimulatorHistoryPage() {
  const { lang } = useLanguage();
  const { user, isPro } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [winRate, setWinRate] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      if (user === null) return;
      if (user === false) {
        setError("login");
        setLoading(false);
        return;
      }
      if (!isPro) {
        setError("paywall");
        setLoading(false);
        return;
      }
      try {
        const API = API_BASE;  // runtime-resolved, see /lib/api.js
        const token = localStorage.getItem("rebel_jwt");
        const res = await fetch(`${API}/api/simulator/history?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await safeJson(res);
        if (res.ok) {
          setItems(data.items || []);
          setTotal(data.total || 0);
          setWinRate(data.win_rate || 0);
        } else if (res.status === 402) {
          setError("paywall");
        } else {
          setError("error");
        }
      } catch {
        setError("error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, isPro]);

  return (
    <div className="min-h-screen bg-[#05060d] text-white antialiased" data-testid="sim-history-page">
      <Navbar />
      <section className="relative w-full overflow-hidden pb-12 pt-32 md:pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(155,89,255,0.16),transparent_60%)]" />
        <div className="relative mx-auto max-w-5xl px-6 md:px-12">
          <Link
            to="/simulator"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/50 transition-colors hover:text-white"
            data-testid="history-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            RE₿EL Market Simulator™
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-6"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-purple-300/80">
              ◈ Trader History
            </p>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-5xl">
              Mi historial de simulaciones
            </h1>
          </motion.div>

          {loading && (
            <p className="mt-12 font-mono text-xs uppercase tracking-[0.3em] text-white/50">
              Cargando…
            </p>
          )}

          {!loading && error === "login" && (
            <div className="mt-10 rounded-2xl border border-amber-400/30 bg-amber-400/[0.05] p-6">
              <p className="text-sm text-amber-100">Inicia sesión para ver tu historial.</p>
            </div>
          )}

          {!loading && error === "paywall" && (
            <div className="mt-10 rounded-2xl border border-amber-400/30 bg-amber-400/[0.05] p-6">
              <div className="flex items-start gap-3">
                <Lock className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                <div>
                  <p className="font-display text-lg font-bold tracking-tight text-amber-100">
                    Historial disponible solo en PRO
                  </p>
                  <p className="mt-2 text-sm text-white/65">
                    Las simulaciones FREE no se guardan. Desbloquea RE₿EL PRO para conservar todo tu historial,
                    rankings y análisis Trader DNA™.
                  </p>
                  <button
                    onClick={() => navigate("/simulator")}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
                    data-testid="history-unlock-btn"
                  >
                    <Lock className="h-4 w-4" />
                    Desbloquear PRO
                  </button>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
              <p className="text-sm text-white/55">
                Aún no tienes simulaciones guardadas. Lanza tu primera sesión y aparecerá aquí.
              </p>
              <button
                onClick={() => navigate("/simulator/play")}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-black"
              >
                Lanzar simulador
              </button>
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <>
              {/* Aggregates */}
              <div className="mt-10 grid grid-cols-3 gap-3">
                <Stat label="Sesiones" value={String(total)} icon={Activity} />
                <Stat
                  label="Win rate"
                  value={`${winRate.toFixed(0)}%`}
                  icon={Trophy}
                  color={winRate >= 50 ? "#00e676" : "#f5a623"}
                />
                <Stat label="Acceso" value="PRO" icon={Crown} color="#ffd700" />
              </div>

              {/* Sessions list */}
              <div className="mt-8 space-y-3" data-testid="history-list">
                {items.map((s, i) => {
                  const positive = (s.pnl_pct || 0) >= 0;
                  return (
                    <motion.div
                      key={s.id}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: i * 0.03 }}
                      className="group flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.025] p-5 backdrop-blur-md transition-all hover:border-white/20 hover:bg-white/[0.04] sm:flex-row sm:items-center sm:justify-between"
                      data-testid={`history-item-${i}`}
                    >
                      <div className="min-w-0">
                        <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-cyan-300/70">
                          {new Date(s.created_at).toLocaleString(lang)}
                        </p>
                        <p className="mt-1 truncate font-display text-base font-bold tracking-tight">
                          {s.scenario_name}
                        </p>
                        <p className="mt-1 font-mono text-[11px] text-white/40">
                          {s.duration_candles} velas · {s.trades_count} trades
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/35">
                            PnL
                          </p>
                          <p
                            className={`font-display text-xl font-black tracking-tight ${
                              positive ? "text-emerald-300" : "text-rose-300"
                            }`}
                          >
                            {positive ? "+" : ""}
                            {(s.pnl_pct || 0).toFixed(2)}%
                          </p>
                        </div>
                        {positive ? (
                          <TrendingUp className="h-5 w-5 text-emerald-400" />
                        ) : (
                          <TrendingDown className="h-5 w-5 text-rose-400" />
                        )}
                        <span
                          className="rounded-md border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
                          style={{
                            background: s.rank === "MARKET PREDATOR" ? "linear-gradient(135deg,#ffd700,#ff8c00)" : "rgba(255,255,255,0.04)",
                            color: s.rank === "MARKET PREDATOR" ? "#0a0a10" : "#fff",
                            borderColor: "rgba(255,255,255,0.18)",
                          }}
                        >
                          {s.rank || "—"}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, color = "#ffffff" }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
    <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.22em] text-white/45">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <p className="mt-2 font-display text-2xl font-black tracking-tighter" style={{ color }}>
      {value}
    </p>
  </div>
);
